import {
  ConverterToolbar,
  FileQueuePanel,
  ProgressBar,
  ResultsPanel,
  SelectField,
  SettingsPopover,
  SliderField,
  StatusBanner,
} from "@batchworks/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiRadio, FiSliders, FiVolume2 } from "react-icons/fi";
import {
  AUDIO_FILE_ICON,
  AUDIO_THEME,
  CHANNEL_OPTIONS,
  FORMAT_OPTIONS,
  SAMPLE_RATE_OPTIONS,
} from "../appConfig";
import { useBatchConvert } from "../hooks/useBatchConvert";
import type { FileSetter, TargetMimeType } from "../types";
import {
  formatBytes,
  getFileExtension,
  getUnsupportedConversionReason,
  isTargetFormatSupportedForFiles,
} from "../utils/audioOps";

interface ConverterDashboardProps {
  files: File[];
  setFiles: FileSetter;
}

const FORMAT_BADGE: Record<string, string> = {
  wav: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  flac: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  m4a: "bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-900/30 dark:text-fuchsia-400",
  aac: "bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-900/30 dark:text-fuchsia-400",
  ogg: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  opus: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  webm: "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400",
};

const getBadgeClass = (ext: string) =>
  FORMAT_BADGE[ext] ??
  "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400";

const bitrateHint = (bitrateKbps: number) => {
  if (bitrateKbps >= 256) return "Higher quality, larger lossy audio files";
  if (bitrateKbps >= 160)
    return "Balanced quality and size for MP3, M4A and OGG";
  return "Smaller files, stronger lossy compression";
};

const ConverterDashboard = ({ files, setFiles }: ConverterDashboardProps) => {
  const {
    targetFormat,
    setTargetFormat,
    bitrateKbps,
    setBitrateKbps,
    sampleRate,
    setSampleRate,
    channels,
    setChannels,
    isConverting,
    progress,
    status,
    clearStatus,
    converted,
    clearConverted,
    handleConvert,
    downloadAll,
    downloadSingle,
  } = useBatchConvert(files);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const handleReset = () => {
    setFiles([]);
    clearConverted();
    clearStatus();
  };

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    settingsButtonRef.current?.focus();
  }, []);

  const hasCustomSettings =
    bitrateKbps !== 192 || sampleRate !== "original" || channels !== "original";

  const formatOptions = useMemo(
    () =>
      FORMAT_OPTIONS.map((option) => {
        const disabledReason = getUnsupportedConversionReason(
          files,
          option.value,
        );
        return disabledReason
          ? {
              ...option,
              disabled: true,
              disabledLabel: `${option.label} (not for MP3)`,
              disabledReason,
            }
          : option;
      }),
    [files],
  );

  useEffect(() => {
    if (!isTargetFormatSupportedForFiles(files, targetFormat)) {
      setTargetFormat("audio/mpeg");
    }
  }, [files, setTargetFormat, targetFormat]);

  const settingsPanel = useMemo(
    () => (
      <SettingsPopover
        isOpen={settingsOpen}
        onClose={closeSettings}
        triggerRef={settingsButtonRef}
        title="Settings"
        subtitle="Adjust Output"
        icon={FiSliders}
        theme={AUDIO_THEME}
        note={
          <>
            <strong className="text-slate-700 dark:text-slate-300">
              Note:
            </strong>{" "}
            Bitrate affects MP3, M4A and OGG. WAV and FLAC remain lossless.
          </>
        }
      >
        <div className="space-y-1">
          <div className="mb-3 flex items-center gap-2">
            <FiVolume2 className="text-xs text-rose-500" />
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">
              Encoding
            </span>
          </div>
          <SliderField
            label="Bitrate"
            value={bitrateKbps}
            min={64}
            max={320}
            step={32}
            unit=" kbps"
            onChange={setBitrateKbps}
            theme={AUDIO_THEME}
            hint={bitrateHint(bitrateKbps)}
          />
        </div>

        <div className="space-y-1">
          <div className="mb-3 flex items-center gap-2">
            <FiRadio className="text-xs text-sky-500" />
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">
              Signal
            </span>
          </div>
          <div className="space-y-4">
            <SelectField
              label="Sample Rate"
              value={sampleRate}
              options={SAMPLE_RATE_OPTIONS}
              onChange={setSampleRate}
              theme={AUDIO_THEME}
              hint={
                sampleRate === "original"
                  ? "Keep the source sample rate"
                  : `Resample output to ${Number(sampleRate) / 1000} kHz`
              }
            />
            <SelectField
              label="Channels"
              value={channels}
              options={CHANNEL_OPTIONS}
              onChange={setChannels}
              theme={AUDIO_THEME}
              hint={
                channels === "original"
                  ? "Keep the original channel layout"
                  : channels === "1"
                    ? "Mix down to mono"
                    : "Force stereo output"
              }
            />
          </div>
        </div>
      </SettingsPopover>
    ),
    [
      bitrateKbps,
      channels,
      closeSettings,
      sampleRate,
      settingsOpen,
      setBitrateKbps,
      setChannels,
      setSampleRate,
    ],
  );

  if (!files.length && !converted.length) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      aria-label="Converter workspace"
      className="mx-auto mb-20 w-full max-w-5xl space-y-6"
    >
      <AnimatePresence>
        {status.message && (
          <StatusBanner
            status={status}
            onDismiss={clearStatus}
            theme={AUDIO_THEME}
          />
        )}
      </AnimatePresence>

      <ConverterToolbar
        fileCount={files.length}
        convertedCount={converted.length}
        targetFormat={targetFormat}
        formatOptions={formatOptions}
        onFormatChange={(value) => setTargetFormat(value as TargetMimeType)}
        isConverting={isConverting}
        progress={progress}
        mediaLabel="Audio File"
        settingsOpen={settingsOpen}
        hasCustomSettings={hasCustomSettings}
        onToggleSettings={() => setSettingsOpen((open) => !open)}
        onReset={handleReset}
        onConvert={handleConvert}
        settingsButtonRef={settingsButtonRef}
        settingsPanel={settingsPanel}
        theme={AUDIO_THEME}
      />

      <ProgressBar
        isVisible={isConverting}
        progress={progress}
        theme={AUDIO_THEME}
      />

      {converted.length > 0 ? (
        <ResultsPanel
          converted={converted}
          onDownloadSingle={downloadSingle}
          onDownloadAll={downloadAll}
          icon={AUDIO_FILE_ICON}
          formatBytes={formatBytes}
          mediaLabel="Audio File"
          theme={AUDIO_THEME}
        />
      ) : (
        <div className="relative z-0">
          <FileQueuePanel
            files={files}
            setFiles={setFiles}
            isConverting={isConverting}
            icon={AUDIO_FILE_ICON}
            getFileExtension={getFileExtension}
            formatBytes={formatBytes}
            badgeClassForExt={getBadgeClass}
            theme={AUDIO_THEME}
          />
        </div>
      )}
    </motion.section>
  );
};

export default ConverterDashboard;
